import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from 'services/config';
import type { CanonicalMeasurement } from '@/models/template';
import { prepareHeaders } from '@/state/slices/index.ts';

export const OptionAPI = createApi({
  reducerPath: 'OptionAPI',
  baseQuery: fetchBaseQuery({
    baseUrl: config.BASE_URL,
    prepareHeaders,
  }),
  tagTypes: [
    'Priorities',
    'Statuses',
    'Modalities',
    'Sites',
    'TagClasses',
    'SessionDurations',
    'InactivityTimeouts',
    'WorksheetSectionTypes',
    'WorksheetContextClasses',
    'WorksheetFindingClasses',
    'WorksheetFlagClasses',
    'WorksheetFindingGroupStyles',
    'WorksheetCanonicalMeasurements',
  ],
  endpoints: (builder) => ({
    getPriorityOptions: builder.query<string[], void>({
      query: () => ({
        url: `/options/assignments/priorities/`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [{ type: 'Priorities', id: 'LIST' }]
          : [{ type: 'Priorities', id: 'LIST' }],
    }),
    getStatusOptions: builder.query<string[], void>({
      query: () => ({
        url: `/options/assignments/statuses/`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [{ type: 'Statuses', id: 'LIST' }]
          : [{ type: 'Statuses', id: 'LIST' }],
    }),
    getStudyModalitiesOptions: builder.query<string[], void>({
      query: () => ({
        url: `/options/assignments/study-modalities/`,
        method: 'GET',
      }),
      providesTags: () => [{ type: 'Modalities', id: 'LIST' }],
    }),
    getStudySitesOptions: builder.query<string[], void>({
      query: () => ({
        url: `/options/assignments/study-sites/`,
        method: 'GET',
      }),
      providesTags: () => [{ type: 'Sites', id: 'LIST' }],
    }),
    getTagClassOptions: builder.query<string[], void>({
      query: () => ({
        url: `/options/tags/tag-classes/`,
        method: 'GET',
      }),
      providesTags: () => [{ type: 'TagClasses', id: 'LIST' }],
    }),
    getSessionDurationOptions: builder.query<string[], void>({
      query: () => ({
        url: `/options/settings/session-duration/`,
        method: 'GET',
      }),
      providesTags: () => [{ type: 'SessionDurations', id: 'LIST' }],
    }),
    getInactivityTimeoutOptions: builder.query<string[], void>({
      query: () => ({
        url: `/options/settings/inactivity-timeout/`,
        method: 'GET',
      }),
      providesTags: () => [{ type: 'InactivityTimeouts', id: 'LIST' }],
    }),

    /* ── Worksheet builder options ──────────────────────────────────── */

    getWorksheetSectionTypes: builder.query<string[], void>({
      query: () => ({
        url: `/options/worksheets/section-types/`,
        method: 'GET',
      }),
      providesTags: () => [{ type: 'WorksheetSectionTypes', id: 'LIST' }],
    }),
    getWorksheetContextClasses: builder.query<string[], void>({
      query: () => ({
        url: `/options/worksheets/context-classes/`,
        method: 'GET',
      }),
      providesTags: () => [{ type: 'WorksheetContextClasses', id: 'LIST' }],
    }),
    getWorksheetFindingClasses: builder.query<string[], void>({
      query: () => ({
        url: `/options/worksheets/finding-classes/`,
        method: 'GET',
      }),
      providesTags: () => [{ type: 'WorksheetFindingClasses', id: 'LIST' }],
    }),
    getWorksheetFlagClasses: builder.query<string[], void>({
      query: () => ({
        url: `/options/worksheets/flag-classes/`,
        method: 'GET',
      }),
      providesTags: () => [{ type: 'WorksheetFlagClasses', id: 'LIST' }],
    }),
    getWorksheetFindingGroupStyles: builder.query<string[], void>({
      query: () => ({
        url: `/options/worksheets/finding-group-styles/`,
        method: 'GET',
      }),
      providesTags: () => [{ type: 'WorksheetFindingGroupStyles', id: 'LIST' }],
    }),
    getWorksheetCanonicalMeasurements: builder.query<
      CanonicalMeasurement[],
      void
    >({
      query: () => ({
        url: `/options/worksheets/canonical-measurements/`,
        method: 'GET',
      }),
      providesTags: () => [
        { type: 'WorksheetCanonicalMeasurements', id: 'LIST' },
      ],
    }),
  }),
});
export const {
  useGetPriorityOptionsQuery,
  useGetStatusOptionsQuery,
  useGetStudyModalitiesOptionsQuery,
  useGetStudySitesOptionsQuery,
  useGetTagClassOptionsQuery,
  useGetSessionDurationOptionsQuery,
  useGetInactivityTimeoutOptionsQuery,
  useGetWorksheetSectionTypesQuery,
  useGetWorksheetContextClassesQuery,
  useGetWorksheetFindingClassesQuery,
  useGetWorksheetFlagClassesQuery,
  useGetWorksheetFindingGroupStylesQuery,
  useGetWorksheetCanonicalMeasurementsQuery,
} = OptionAPI;
